import React, { useState, useEffect } from 'react'
import { useDiscountPreviews } from 'hooks'
import { trackCourseSwitch } from 'utils/analytics'
import PackageSelector from '../shared/PackageSelector'
import DrillDownCourseSelect from '../shared/DrillDownCourseSelect'

export default function MobileCoursePickerModal({ isOpen, onClose, courses, courseCategories, colors, onPurchase, onClaimTrial, purchasing, trialUsed, translations, initialCourseId, currentUser, teamId }) {
  const [selectedCourseId, setSelectedCourseId] = useState(courses?.[0]?.id || null)
  const [selectedPackageId, setSelectedPackageId] = useState(null)

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedCourseId(initialCourseId || courses?.[0]?.id || null)
      setSelectedPackageId(null)
    }
  }, [isOpen, courses, initialCourseId])

  // Clear package selection when course changes
  useEffect(() => {
    setSelectedPackageId(null)
  }, [selectedCourseId])

  const selectedCourse = courses?.find(c => c.id === selectedCourseId)
  const trialPkg = !trialUsed ? selectedCourse?.packages?.find(p => p.isTrial) : null
  const regularPackages = selectedCourse?.packages?.filter(p => !p.isTrial) || []
  const visiblePackages = [...(trialPkg ? [trialPkg] : []), ...regularPackages]
  // Hook must run before any early return (Rules of Hooks).
  const discountPreviews = useDiscountPreviews(teamId, !!currentUser?.id)

  if (!isOpen) return null

  const slideUpStyle = `@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`

  const handleCourseSelect = (courseId) => {
    trackCourseSwitch(courses, courseId, 'mobile_picker')
    setSelectedCourseId(courseId)
  }

  const handlePackageSelect = (pkgId) => {
    if (purchasing) return
    const pkg = visiblePackages.find(p => p.id === pkgId)
    if (!selectedCourse || !pkg) return
    setSelectedPackageId(pkgId)
    if (pkg.isTrial && pkg.totalPrice === 0) {
      onClaimTrial?.(selectedCourse)
    } else {
      onPurchase?.(selectedCourse, pkg)
    }
    onClose()
  }

  return (
    <>
      <style>{slideUpStyle}</style>
      {/* Overlay */}
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />

      {/* Bottom Sheet */}
      <div data-section="mobile_picker" className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl"
        style={{ maxHeight: '80vh', paddingBottom: 'env(safe-area-inset-bottom, 0px)', animation: 'slideUp 0.25s ease-out' }}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-5 pb-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">
            {translations?.selectCourse || 'Select Course'}
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto px-5 py-4" style={{ maxHeight: 'calc(80vh - 140px)' }}>
          {/* Course Selector (only if multiple courses) */}
          {courses.length > 1 && (
            <div className="mb-4">
              {/* The sheet header already says this; a second copy just eats a
                  line at phone width. */}
              <DrillDownCourseSelect
                courses={courses}
                courseCategories={courseCategories}
                selectedCourseId={selectedCourseId}
                onSelect={handleCourseSelect}
                colors={colors}
                translations={translations}
              />
            </div>
          )}

          {/* Single course — show name */}
          {courses.length === 1 && selectedCourse && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700">
                {selectedCourse.name}
                <span className="text-gray-400 ml-1">({selectedCourse.durationMinutes}{translations?.min || 'min'})</span>
              </p>
            </div>
          )}

          {/* Package Options */}
          {visiblePackages.length > 0 && (
            <PackageSelector
              key={selectedCourse?.id}
              packages={visiblePackages}
              courseDuration={selectedCourse?.durationMinutes}
              selectedId={selectedPackageId}
              onSelect={handlePackageSelect}
              trialUsed={trialUsed}
              colors={colors}
              translations={translations}
              discountPreviews={discountPreviews}
            />
          )}
        </div>

      </div>
    </>
  )
}
